import React, { useMemo, useState } from 'react';
import { computeDiscreteColorBands, formatValue, areValuesEffectivelyEqual } from '../../../utils/mapUtils';
import { getPaletteColors, getDefaultPalette, mapIntervalToColorIndex } from '../../../utils/colorPalettes';

const Colorbar = ({ layer, onFilterLayer }) => {
  const [hoveredBand, setHoveredBand] = useState(null);

  // Compute discrete color bands using shared function (same as MapPanel)
  // This ensures exact same colors and quantiles as the map rendering
  const { colorBands = [], isCategorical = false } = useMemo(() => {
    const result = computeDiscreteColorBands(
      layer,
      getPaletteColors,
      mapIntervalToColorIndex,
      getDefaultPalette,
      6
    );
    return {
      colorBands: result.colorBands || [],
      isCategorical: result.isCategorical || false
    };
  }, [layer]);


  const handleBandClick = (band, index) => {
    if (onFilterLayer) {
      if (isCategorical) {
        // For categorical, filter by exact value match
        onFilterLayer(layer.id, {
          min: band.value,
          max: band.value,
          isLast: true
        });
      } else {
        const isLast = index === colorBands.length - 1;
        // Filter to show only hexes in this quantile range
        // For non-last bands, upper bound is exclusive (<)
        // For last band, upper bound is inclusive (<=)
        onFilterLayer(layer.id, {
          min: band.value,
          max: band.nextValue,
          isLast: isLast
        });
      }
    }
  };

  return (
    <div className="flex flex-col gap-0.5">
      {/* Vertical color bands with compact labels */}
      {colorBands.map((band, index) => {
        const isHovered = hoveredBand === index;
        const isLast = index === colorBands.length - 1;
        const isFirst = index === 0;

        // Check if this band matches the active filter
        const isFiltered = layer.valueFilter &&
          (isCategorical
            ? Math.abs(layer.valueFilter.min - band.value) < 0.0001 && Math.abs(layer.valueFilter.max - band.value) < 0.0001
            : Math.abs(layer.valueFilter.min - band.value) < 0.0001 && Math.abs(layer.valueFilter.max - band.nextValue) < 0.0001);

        // If there's an active filter and this band is not the filtered one, grey it out
        const hasActiveFilter = layer.valueFilter !== null && layer.valueFilter !== undefined;
        const shouldGreyOut = hasActiveFilter && !isFiltered;

        // Format range label with comparison operators
        // For categorical, just show the value
        // Handle edge case: when all values are the same (band.value === band.nextValue)
        // Use tolerance-based comparison for floating point numbers
        const isSingleValue = areValuesEffectivelyEqual(band.value, band.nextValue);
        let rangeLabel;
        if (isCategorical || isSingleValue) {
          // Categorical or all values are the same, just show the value
          rangeLabel = formatValue(band.value);
        } else if (isLast) {
          rangeLabel = `≥ ${formatValue(band.value)}`;
        } else if (isFirst) {
          rangeLabel = `< ${formatValue(band.nextValue)}`;
        } else {
          rangeLabel = `≥ ${formatValue(band.value)} & < ${formatValue(band.nextValue)}`;
        }

        return (
          <div
            key={index}
            className="flex items-center gap-2 cursor-pointer transition-all group"
            onClick={() => handleBandClick(band, index)}
            onMouseEnter={() => setHoveredBand(index)}
            onMouseLeave={() => setHoveredBand(null)}
          >
            {/* Color swatch */}
            <div
              className="w-4 h-4 rounded border border-gray-300 dark:border-gray-600 flex-shrink-0 transition-all"
              style={{
                backgroundColor: shouldGreyOut ? '#9ca3af' : band.color,
                opacity: shouldGreyOut ? 0.3 : isHovered ? 0.8 : isFiltered ? 1 : 1,
                transform: isHovered ? 'scale(1.1)' : isFiltered ? 'scale(1.05)' : 'scale(1)',
                boxShadow: isFiltered ? '0 0 0 2px rgba(16, 185, 129, 0.6)' : 'none',
                filter: shouldGreyOut ? 'grayscale(100%)' : 'none'
              }}
            />
            {/* Range label */}
            <span
              className={`text-[9px] font-medium tabular-nums transition-colors ${
                shouldGreyOut
                  ? 'text-gray-400 dark:text-gray-600'
                  : isFiltered
                  ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                  : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              {rangeLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default Colorbar;
